import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { timeoutDuration } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { showToastError } from 'utils/generic/show-toast-error';
import { showToastWarning } from 'utils/generic/show-toast-warning';
import { debounce } from 'utils/helpers/debounce';
import { thousandSeparator } from 'utils/helpers/validators';
import { openParentPage } from 'utils/sportsbook/api-post-message-sender';
import RidGenerator from 'utils/swarm/rid-generator';
import { useBalance } from './useBalance';
import { useCurrency } from './useCurrency';
import { addQuery } from 'components/QueryRoute';
import {
  getEditBet,
  getIsFreeBetEnabled,
  getIsFrenchFreeBetEnabled,
  getUseBonusBalance
} from 'store/selectors/betslip';
import { getPartnerConfigs } from 'store/selectors/socket';
import { getUser } from 'store/selectors/user-data';

export function useCheckStakeBalance(
  debounceDuration: number = timeoutDuration.DURATION_500
): (
  contextId: string | null,
  stake: string | number,
  callback?: (disable: boolean) => void
) => void {
  const history = useHistory();
  const { t } = useTranslation();
  const { currencyId } = useCurrency();
  const balance = useBalance();
  const user = useSelector(getUser);
  const isFreeBetEnabled = useSelector(getIsFreeBetEnabled);
  const partnerConfigs = useSelector(getPartnerConfigs);
  const useBonusBalance = useSelector(getUseBonusBalance);
  const useFrenchFreeBet = useSelector(getIsFrenchFreeBetEnabled);
  const editBet = useSelector(getEditBet);
  const debounceId = useRef(RidGenerator.rand());

  const getToDeposit = () => {
    if (useBonusBalance || useFrenchFreeBet) {
      return;
    }

    if (SpringConfigs.IFRAME_SPORTSBOOK) {
      openParentPage('deposit');
    } else {
      history.push(addQuery({ accounts: '*', wallet: '*', deposit: '*' }));
    }
  };

  const checkMinBetStakeLimit = (
    stake: string | number,
    contextId: string | null
  ) => {
    const betLimitMinStakeForEditBet =
      editBet?.active &&
      editBet?.amount &&
      +editBet.amount + +stake <
        (partnerConfigs?.min_bet_stakes[currencyId] || 0);

    const isBetMinLimitStake =
      +stake !== 0 &&
      ((!editBet?.active &&
        +stake < (partnerConfigs?.min_bet_stakes[currencyId] || 0)) ||
        betLimitMinStakeForEditBet);

    if (isBetMinLimitStake) {
      const minLimit = t('betslip.betStakeMinLimitErrors', {
        min_bet: thousandSeparator(
          partnerConfigs?.min_bet_stakes[currencyId] || ''
        ),
        currency: currencyId
      });

      showToastWarning(
        minLimit,
        contextId ? document.getElementById(contextId) : document.body
      );
    }

    return isBetMinLimitStake;
  };

  const checkInsufficientBalance = (
    stake: string | number,
    contextId: string | null
  ) => {
    const isInsufficient = +stake > (balance || user.balance);

    if (isInsufficient) {
      let balanceError = '';

      if (useBonusBalance && user?.bonus_balance) {
        balanceError = t('betslip.insufficientBonusFundError');
      } else if (useFrenchFreeBet) {
        balanceError = t('betslip.insufficientAccountFundError');
      } else {
        balanceError = t('betslip.insufficientFundError');
      }

      showToastError(
        `<span>${balanceError}</span>`,
        contextId ? document.getElementById(contextId) : document.body,
        null,
        getToDeposit,
        { cursor: 'pointer' }
      );
    }

    return isInsufficient;
  };

  return debounce(
    debounceId.current,
    (
      contextId: string | null,
      stake: string | number,
      callback?: (disable: boolean) => void
    ): void => {
      if (stake === '' || isFreeBetEnabled) {
        return callback?.(false);
      }

      const isBetMinLimitStake = checkMinBetStakeLimit(stake, contextId);
      const isInsufficient = checkInsufficientBalance(stake, contextId);

      callback?.(isBetMinLimitStake || isInsufficient || !Number(stake));
    },
    debounceDuration
  );
}
